import React, { useState } from 'react';
import { getContrastRatio } from '@/lib/qr/health';
import {
  Wrench,
  Printer,
  Eye,
  ShieldCheck,
  Globe,
  HelpCircle
} from 'lucide-react';

export const ToolsManager: React.FC = () => {
  const [scanDistanceMeters, setScanDistanceMeters] = useState<number>(1.5);
  const [qrComplexity, setQrComplexity] = useState<'low' | 'medium' | 'high'>('medium');

  const [fgColor, setFgColor] = useState<string>('#1d1d1f');
  const [bgColor, setBgColor] = useState<string>('#ffffff');

  const ratioFactor = qrComplexity === 'low' ? 10 : qrComplexity === 'medium' ? 7 : 5;
  const printSizeMm = Math.round((scanDistanceMeters * 1000) / ratioFactor);
  const printSizeInches = (printSizeMm / 25.4).toFixed(1);

  const contrast = getContrastRatio(fgColor, bgColor);
  const isAASafe = contrast >= 4.5;
  const isAAASafe = contrast >= 7.0;

  return (
    <div className="w-full space-y-6">
      {/* Header Store Utility Card */}
      <div className="p-6 rounded-2xl bg-card border border-border">
        <h3 className="text-[21px] font-semibold text-foreground tracking-tight flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          QR Diagnostics & Engineering Tools
        </h3>
        <p className="text-[14px] text-muted-foreground mt-0.5">
          Calculators, physical print dimension rules, contrast auditors, and architectural references
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 1. Print Sizing & Distance Calculator */}
        <div className="p-6 rounded-2xl bg-card border border-border space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-border">
            <Printer className="h-5 w-5 text-primary" />
            <div>
              <h4 className="text-[17px] font-semibold text-foreground tracking-tight">
                Print Sizing Calculator
              </h4>
              <p className="text-[12px] text-muted-foreground">ISO/IEC 18004 optical resolution rule</p>
            </div>
          </div>

          <div className="space-y-4 text-[14px]">
            <div>
              <label className="block font-normal text-muted-foreground mb-1">
                Scan Distance: {scanDistanceMeters}m ({(scanDistanceMeters * 3.28).toFixed(1)} ft)
              </label>
              <input
                type="range"
                min={0.2}
                max={10}
                step={0.1}
                value={scanDistanceMeters}
                onChange={(e) => setScanDistanceMeters(Number(e.target.value))}
                className="w-full h-1.5 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
              />
            </div>

            <div>
              <label className="block font-normal text-muted-foreground mb-1.5">
                Data Density Level
              </label>
              <div className="grid grid-cols-3 gap-2">
                {(['low', 'medium', 'high'] as const).map((lvl) => (
                  <button
                    key={lvl}
                    onClick={() => setQrComplexity(lvl)}
                    className={`p-2 rounded-xl border capitalize font-normal cursor-pointer transition-all ${
                      qrComplexity === lvl
                        ? 'bg-primary/10 border-primary text-primary font-medium'
                        : 'border-border text-muted-foreground'
                    }`}
                  >
                    {lvl}
                  </button>
                ))}
              </div>
            </div>

            {/* Result Box */}
            <div className="p-4 rounded-xl bg-muted border border-border space-y-1">
              <span className="text-[12px] font-normal text-muted-foreground block">
                Recommended Minimum Print Size
              </span>
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-2 pt-1">
                <span className="text-[22px] sm:text-[28px] font-semibold text-foreground tracking-tight whitespace-nowrap">
                  {printSizeMm} mm &times; {printSizeMm} mm
                </span>
                <span className="text-[15px] sm:text-[17px] text-muted-foreground whitespace-nowrap">
                  ({printSizeInches}&quot; &times; {printSizeInches}&quot;)
                </span>
              </div>
              <p className="text-[12px] text-muted-foreground pt-1">
                At 300 DPI, export resolution should be at least {Math.round(printSizeMm * 11.81)} &times; {Math.round(printSizeMm * 11.81)} px.
              </p>
            </div>
          </div>
        </div>

        {/* 2. Color Contrast Tester */}
        <div className="p-6 rounded-2xl bg-card border border-border space-y-5">
          <div className="flex items-center gap-2 pb-3 border-b border-border">
            <Eye className="h-5 w-5 text-primary" />
            <div>
              <h4 className="text-[17px] font-semibold text-foreground tracking-tight">
                Contrast & Legibility Auditor
              </h4>
              <p className="text-[12px] text-muted-foreground">Audit foreground and background for camera sensors</p>
            </div>
          </div>

          <div className="space-y-4 text-[14px]">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[12px] font-normal text-muted-foreground mb-1.5">
                  Foreground
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="h-10 w-12 rounded-lg bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={fgColor}
                    onChange={(e) => setFgColor(e.target.value)}
                    className="w-full px-2.5 py-2 font-mono uppercase bg-muted rounded-lg border border-border text-[12px]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-normal text-muted-foreground mb-1.5">
                  Background
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="h-10 w-12 rounded-lg bg-transparent border-0 cursor-pointer"
                  />
                  <input
                    type="text"
                    value={bgColor}
                    onChange={(e) => setBgColor(e.target.value)}
                    className="w-full px-2.5 py-2 font-mono uppercase bg-muted rounded-lg border border-border text-[12px]"
                  />
                </div>
              </div>
            </div>

            {/* Contrast Score Box */}
            <div className="p-4 rounded-xl bg-muted border border-border space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">
                  WCAG Contrast Ratio:
                </span>
                <span className="text-[21px] font-semibold text-foreground">
                  {contrast.toFixed(2)}:1
                </span>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <span
                  className={`px-3 py-0.5 rounded-full text-[12px] font-normal ${
                    isAAASafe
                      ? 'bg-success/10 text-success'
                      : isAASafe
                      ? 'bg-primary/10 text-primary'
                      : 'bg-destructive/10 text-destructive'
                  }`}
                >
                  {isAAASafe ? '✓ AAA High Legibility' : isAASafe ? '✓ AA Good Contrast' : '✕ Poor Contrast'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Static vs Dynamic QR Reference */}
      <div className="p-6 rounded-2xl bg-card border border-border space-y-4">
        <h4 className="text-[17px] font-semibold text-foreground tracking-tight flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-muted-foreground" />
          Architecture: Static vs Dynamic QR Codes
        </h4>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[14px] leading-relaxed">
          <div className="p-4 rounded-xl bg-muted border border-border space-y-1.5">
            <h5 className="font-semibold text-foreground flex items-center gap-1.5">
              <ShieldCheck className="h-4 w-4 text-success" />
              Static QR Codes (100% Client-Side in QR Studio)
            </h5>
            <p className="text-muted-foreground">
              The payload is encoded <strong>directly</strong> into the module matrix.
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5 pt-1 text-[12px]">
              <li>Works indefinitely with no server subscription</li>
              <li>100% private: zero data leaves device</li>
              <li>Fixed destination once printed</li>
            </ul>
          </div>

          <div className="p-4 rounded-xl bg-muted border border-border space-y-1.5">
            <h5 className="font-semibold text-foreground flex items-center gap-1.5">
              <Globe className="h-4 w-4 text-primary" />
              Dynamic QR Codes (Redirect Service)
            </h5>
            <p className="text-muted-foreground">
              Encodes a short link that forwards visitors to the destination.
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-0.5 pt-1 text-[12px]">
              <li>Destination can be altered anytime</li>
              <li>Supports analytics & click counters</li>
              <li>Requires continuous server uptime</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
